import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { observer } from 'mobx-react'
import { computed, observable, action, makeObservable } from 'mobx'

import CKEditor from 'ckeditor4-react'
import { toolbarPresets, getCustomToolbar } from './presets/toolbar'
import { config } from './editor-config'
import { countWords } from '../Form/utils'

const RichTextEditor = observer(class RichTextEditor extends Component {
  ref

  constructor (props) {
    super(props)

    makeObservable(this, {
      mentions: computed,
      html: computed,
      height: computed,
      disabled: computed,
      updated: observable,
      textValue: observable,
      editorText: observable,
      locked: observable,
      componentDidUpdate: action,
      onChange: action,
      onKeyDown: action
    })
  }

  get mentions () {
    const { mentionGroup, mentionField } = this.props
    if (!mentionGroup || !mentionField) return []
    const mentions = []
    mentionGroup && mentionGroup.forEach(user => {
      if (user[mentionField]) {
        mentions.push(user[mentionField])
      }
    })
    return (mentions && mentions.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))) || []
  }

  get disabled () {
    return this.props.disabled || false
  }
  
  get html () {
    return this.props.html || ''
  }

  get height () {
    return this.props.height || 0
  }

  updated = false
  textValue = ''
  editorText = ''
  locked = false

  static propTypes = {
    onChange: PropTypes.func.isRequired,
    html: PropTypes.string,
    height: PropTypes.string,
    width: PropTypes.string,
    allowHtmlClasses: PropTypes.bool
  }

  static defaultProps = {
    height: '400px',
    width: '100%',
    allowHtmlClasses: true
  }

  state = {
    configChanged: false
  }

  componentDidMount () {
    if (this.state.configChanged) {
      this.helpSetState()
    }
  }

  componentDidUpdate (prevProps, prevState, snapshot) {
    if (prevProps.html !== this.props.html && !this.updated) {
      this.updated = true
      this.helpSetState(true)
    }
    if (prevProps.mentionGroup !== this.props.mentionGroup) {
      this.helpSetState(true)
    }
    if (this.state.configChanged && this.height > 0) {
      this.helpSetState()
    }
  
    if (['loaded', 'ready'].includes(this.ref?.editor?.status) && !!this.disabled !== !!this.ref?.editor?.readOnly) {
      this.ref?.editor?.setReadOnly(!!this.disabled)
      this.helpSetState()
    }
  }

  helpSetState (condition = false) {
    this.setState({
      configChanged: condition
    })
  }

  onChange = (event) => {
    if (!this.updated) this.updated = true
    let html = event.editor.getData()
    if (event.editor.mode !== 'source') {
      const { document: doc } = event.editor
      const body = doc && doc.getBody()
      const elements = body.$ && body.getChildren().toArray()
      const isEmpty = !elements?.find(element => {
        const elementsContent = element && element.getHtml?.()
        if (elementsContent?.toString() !== '<br>' && html?.toString()?.[0] === '<') return true
        const firstEl = element?.getFirst?.()
        return (firstEl && (firstEl.getText().trim() > 0))
      })
      if (isEmpty) html = ''
    }

    const { onChange } = this.props
    if (this.html !== html) {
      onChange && onChange(html)
    }
  }

  onKeyDown = async (event) => {
    const { editor, data } = event
    const { config: { charCount, charMaxCount, wordCount, wordsMaxCount } = {} } = this.props
    if (charCount || wordCount) {
      this.textValue = this.html.replace(/<[^>]*>|\s/g, '').replace('nbsp;', '')
      const { document: doc } = event.editor
      const body = doc && doc.getBody()
      this.editorText = body.getText()
      const currentLength = this.textValue.length
      const restrictAddingChar = currentLength >= charMaxCount
      const restrictAddingWord = countWords(this.editorText) > wordsMaxCount
      if ((charCount && restrictAddingChar) || (wordCount && restrictAddingWord)) {
        if (!this.locked) {
          editor.fire('saveSnapshot')
          this.locked = true
          event.cancel()
        } else {
          setTimeout(() => {
            if (((charCount && restrictAddingChar) || (wordCount && restrictAddingWord)) && data.keyCode !== 8) {
              editor.execCommand('undo')
            } else {
              editor.fire('saveSnapshot')
              this.locked = false
            }
          }, 0)
        }
      }
    }

    if (!event.editor) return
    if (event.editor.mode === 'source') {
      setTimeout(() => this.onChange(event), 100)
    }
  }

  onPaste = async ({ data, editor }) => {
    if (this.props.disableImagePaste) return
    const file = data.dataTransfer.getFile(0)
    if (!file?.type.startsWith('image')) return
    if (!this.props.imageUploadFunc) return
    const { cmpId } = this.props
    const ext = file.name.split('.').pop()
    const renamedFile = new File([file], `${cmpId}-${new Date().getTime()}.${ext}`, { type: file.type })
    const reader = new FileReader()
    reader.readAsDataURL(renamedFile)
    const selection = editor.getSelection()
    this.props.preventSubmit(true)
    let range = selection.getRanges()[0]
    // if (!range) {
    //   range = editor.createRange()
    //   range.selectNodeContents(editor.editable())
    //   selection.selectRanges([ range ])
    // }
    range.deleteContents()
    range.select()
    editor.lockSelection(selection)
    reader.onload = () => {
      this.handlePastedImage(editor, reader.result, renamedFile)
    }
  }

  handlePastedImage = (editor, base64, file, loader) => {
    const { imageUploadFunc, imageUploadPath } = this.props
    const br = editor.document.createElement('br')
    editor.insertElement(br)
    const image = editor.document.createElement('img')
    image.setAttribute('src', base64)
    image.setAttribute('id', file.name)
    image.setAttribute('alt', 'uploaded_image')
    image.setAttribute('style', 'max-width: 90%; border-radius: .25rem; opacity: 0.5')
    editor.insertElement(image)
    let uploadedFileUrl
    imageUploadFunc({
      folderPath: `/${imageUploadPath || `_tmp`}`,
      file: file,
      onFileInfoAcquired: ({ fileUrl }) => uploadedFileUrl = decodeURIComponent(fileUrl),
      onUploadComplete: (fileInfo) => this.onUploadComplete(fileInfo, editor, uploadedFileUrl, file.name),
      isAssetPublic: true
    })
  }

  onUploadComplete = (fileInfo, editor, fileUrl, id) => {
    const image = editor.document.getById(id)
    if (!image) return
    editor.widgets.initOn(image, 'image', {
      src: fileUrl
    })
    image.data('cke-saved-src', fileUrl)
    image.removeAttribute('id')
    image.setAttribute('src', fileUrl)
    image.setOpacity(1)
    const span = editor.document.createElement('span')
    const p = editor.document.createElement('p')
    span.setHtml(`&nbsp`)
    image.setAttribute('src', fileUrl)
    image.setAttribute('style', 'max-width: 90%; border-radius: .25rem; opacity: 1')
    image.show()
    editor.insertElement(image)
    editor.insertElement(span)
    editor.insertElement(p)
    editor.unlockSelection(true)
    this.props.preventSubmit(false)
    editor.updateElement()
  }

  render () {
    const {
      startupMode,
      config: {
        toolbarPreset,
        toolbar,
        charCount,
        charMaxCount,
        charLeft,
        wordCount,
        wordsMaxCount,
        wordsLeft,
        protectedSource
      } = {}
    } = this.props

    const editorConfig = {
      
      startupMode: startupMode || 'wysiwyg',
      toolbar: toolbarPreset === 'custom' ? getCustomToolbar(toolbar) : toolbarPresets[toolbarPreset || 'full'],
      allowedContent: this.props.allowHtmlClasses,
      ...(this.mentions?.length ? { mentions: [ { feed: this.mentions, minChars: 0 } ] } : {}),
      ...config,
      height: typeof this.height === 'number' ? `${this.height}rem` : this.height,
      width: this.props.width,
      protectedSource: protectedSource
    }
    const isCounting = charCount || wordCount
    const isInvalid = this.props.error || (this.props.required && !this.html)
    return (
      <Fragment>
        {
          !this.state.configChanged &&
          <div className={`w-100 rich-text-editor ${(isInvalid && `is-invalid`) || ``}`}>
            <CKEditor
              onBeforeLoad={CKEDITOR => {
                CKEDITOR.disableAutoInline = true
              }}
              activeClass={`ckeditor`}
              config={{ ...editorConfig, readOnly: !!this.props.disabled }}
              data={this.html}
              onChange={this.onChange}
              onKey={this.onKeyDown}
              onPaste={this.onPaste}
              ref={r => this.ref = r}
              type={this.props.type}
            />
            {
              this.props.config && !!isCounting &&
              <span className={`hint-text`}>
                {
                  charCount && !charMaxCount &&
                  <small className={`pl-1 pr-1`}>{(this.textValue && this.textValue.length) || 0} chars</small>
                }
                {/* { */}
                {/*  charCount && charMaxCount && */}
                {/*  <small className={`pl-1 pr-1`}>{charsLeft} chars left</small> */}
                {/* } */}
                {
                  charCount && charLeft && charMaxCount &&
                  <small className={`pl-1 pr-1`}>{(this.textValue.length > charMaxCount ? charMaxCount : this.textValue.length) || 0} / {charMaxCount} chars</small>
                }
                {
                  wordCount && !wordsLeft &&
                  <small className={`pl-1 pr-1`}>{(wordCount) && countWords(this.editorText)} words</small>
                }
                {
                  wordCount && wordsLeft && wordsMaxCount &&
                  <small className={`pl-1 pr-1`}>{((wordCount) && countWords(this.editorText)) || 0} / {wordsMaxCount} words</small>
                }
              </span>
            }
            <style jsx>{`
              div.rich-text-editor.is-invalid > :global(div.cke) {
                border-color: rgb(220, 53, 69);
              }
              div.rich-text-editor > :global(div.cke) {
                border-radius: .25rem;
              }
              div.rich-text-editor > :global(div.cke > .cke_inner) {
                border-radius: .25rem;
              }
              div.rich-text-editor > :global(div.cke > .cke_inner > .cke_top) {
                border-top-left-radius: .25rem;
                border-top-right-radius: .25rem;
              }
              div.rich-text-editor > :global(div.cke > .cke_inner > .cke_bottom) {
                border-bottom-left-radius: .25rem;
                border-bottom-right-radius: .25rem;
              }
            `}</style>
          </div>
        }
      </Fragment>
    )
  }
})

export default RichTextEditor
